import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useCallback } from "react";

export type GnaOptimizerPage = "dashboard" | "power" | "simulation";

export interface OptimizerNavigationParams {
  page: GnaOptimizerPage;
  date?: string;
  session?: string;
  tab?: string;
  [key: string]: string | undefined;
}

export const useOptimizerNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const getCurrentPage = useCallback((): GnaOptimizerPage | null => {
    const page = searchParams.get("tab");
    return (page as GnaOptimizerPage) || null;
  }, [searchParams]);

  const getCurrentParams = useCallback((): OptimizerNavigationParams => {
    const params: OptimizerNavigationParams = {
      page: getCurrentPage() || "dashboard",
    };

    // Add all search parameters
    searchParams.forEach((value, key) => {
      if (key !== "tab") {
        params[key] = value;
      }
    });

    return params;
  }, [searchParams, getCurrentPage]);

  const navigateToPage = useCallback(
    (page: GnaOptimizerPage, additionalParams: Record<string, string> = {}) => {
      const currentParams = new URLSearchParams(searchParams);

      // Set the page parameter
      currentParams.set("tab", page);

      // Add or update additional parameters
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value) {
          currentParams.set(key, value);
        } else {
          currentParams.delete(key);
        }
      });

      // Navigate to the same route with updated query parameters
      navigate(`${location.pathname}?${currentParams.toString()}`, {
        replace: false,
      });
    },
    [navigate, location.pathname, searchParams],
  );

  const updateParams = useCallback(
    (params: Record<string, string>) => {
      const currentParams = new URLSearchParams(searchParams);

      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          currentParams.set(key, value);
        } else {
          currentParams.delete(key);
        }
      });

      navigate(`${location.pathname}?${currentParams.toString()}`, {
        replace: true,
      });
    },
    [navigate, location.pathname, searchParams],
  );

  const goToPreviousStage = useCallback(
    async (getGNAOptimizerSession: () => Promise<any>) => {
      try {
        const res = await getGNAOptimizerSession();
        if (res) {
          navigateToPage("power");
        }
      } catch (error) {
        console.error("Error getting previous session:", error);
      }
    },
    [navigateToPage],
  );

  const goToDashboard = useCallback(() => {
    navigateToPage("dashboard");
  }, [navigateToPage]);

  return {
    getCurrentPage,
    getCurrentParams,
    navigateToPage,
    updateParams,
    goToPreviousStage,
    goToDashboard,
    currentPage: getCurrentPage(),
    currentParams: getCurrentParams(),
  };
};
